import { CONFIG } from '../config';

/** AbortSignal que se dispara por timeout o por la señal externa (sin AbortSignal.any: WebView antiguo) */
export function timeoutSignal(ms: number, outer?: AbortSignal): { signal: AbortSignal; done: () => void } {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  const onAbort = () => ctrl.abort();
  if (outer) {
    if (outer.aborted) ctrl.abort();
    else outer.addEventListener('abort', onAbort, { once: true });
  }
  return {
    signal: ctrl.signal,
    done: () => {
      clearTimeout(t);
      outer?.removeEventListener('abort', onAbort);
    },
  };
}

export async function fetchJson<T>(url: string, opts: { signal?: AbortSignal; timeoutMs?: number } = {}): Promise<T> {
  const { signal, done } = timeoutSignal(opts.timeoutMs ?? CONFIG.requestTimeoutMs, opts.signal);
  try {
    const res = await fetch(url, { cache: 'no-store', signal, credentials: 'omit' });
    if (!res.ok) throw new Error(`HTTP ${res.status} en ${url.slice(0, 120)}`);
    return (await res.json()) as T;
  } finally {
    done();
  }
}

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Backoff exponencial con jitter: 1s, 2s, 4s, 8s, 16s… (tope 30 s) */
export function backoffMs(attempt: number): number {
  const base = Math.min(30_000, 1000 * 2 ** attempt);
  return base * (0.8 + Math.random() * 0.4);
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/** Firma binaria de imagen: WebP, JPEG, PNG o AVIF. Evita guardar páginas de error HTML con status 200. */
export async function looksLikeImage(blob: Blob): Promise<boolean> {
  const b = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  if (b.length < 4) return false;
  const ascii = (from: number, to: number) => String.fromCharCode(...Array.from(b.subarray(from, to)));
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return true;
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return true;
  if (b[0] === 0x89 && ascii(1, 4) === 'PNG') return true;
  if (ascii(4, 8) === 'ftyp') return true;
  return false;
}
